import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { api } from '../utils/api';
import { motion } from 'framer-motion';
import { Grid3x3, Trophy, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export default function WordSearchPage() {
  const [puzzles, setPuzzles] = useState([]);
  const [selectedPuzzle, setSelectedPuzzle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gameStarted, setGameStarted] = useState(false);
  const [palavrasEncontradas, setPalavrasEncontradas] = useState([]);
  const [startTime, setStartTime] = useState(null);

  useEffect(() => {
    loadPuzzles();
  }, []);

  const loadPuzzles = async () => {
    try {
      const response = await api.getWordSearchPuzzles();
      setPuzzles(response.data);
    } catch (error) {
      toast.error('Erro ao carregar puzzles');
    } finally {
      setLoading(false);
    }
  };

  const startGame = (puzzle) => {
    setSelectedPuzzle(puzzle);
    setGameStarted(true);
    setPalavrasEncontradas([]);
    setStartTime(Date.now());
  };

  const handleWordFound = (palavra) => {
    if (!palavrasEncontradas.includes(palavra)) {
      setPalavrasEncontradas([...palavrasEncontradas, palavra]);
      toast.success(`Palavra encontrada: ${palavra}`);
    }
  };

  const finishGame = async () => {
    const tempoTotal = Math.floor((Date.now() - startTime) / 1000);
    
    try {
      const result = await api.submitWordSearch({
        puzzle_id: selectedPuzzle.id,
        palavras_encontradas: palavrasEncontradas.length,
        tempo_total: tempoTotal
      });
      
      toast.success(`Jogo finalizado! ${result.data.pontos_ganhos} pontos`);
      setGameStarted(false);
      setSelectedPuzzle(null);
    } catch (error) {
      toast.error('Erro ao finalizar jogo');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  if (puzzles.length === 0) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-screen p-4">
          <Grid3x3 className="h-16 w-16 text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">Nenhum puzzle disponível</h2>
          <p className="text-muted-foreground">Os puzzles estarão disponíveis em breve!</p>
        </div>
      </Layout>
    );
  }

  if (gameStarted && selectedPuzzle) {
    const PalavrasList = () => {
      const words = [];
      for (let i = 0; i < selectedPuzzle.palavras.length; i++) {
        const palavra = selectedPuzzle.palavras[i];
        const isFound = palavrasEncontradas.includes(palavra);
        words.push(
          <span
            key={i}
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              isFound
                ? 'bg-green-100 text-green-700 line-through'
                : 'bg-muted'
            }`}
          >
            {palavra}
          </span>
        );
      }
      return <div className="flex flex-wrap gap-2">{words}</div>;
    };

    return (
      <Layout>
        <div className="p-4 md:p-8 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="wordsearch-game"
          >
            <div className="mb-6">
              <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {selectedPuzzle.tema}
              </h1>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>Dificuldade: {selectedPuzzle.dificuldade}</span>
                <span>Palavras: {palavrasEncontradas.length}/{selectedPuzzle.palavras.length}</span>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 mb-6">
              <div className="mb-4">
                <h3 className="font-semibold mb-2">Palavras para encontrar:</h3>
                <PalavrasList />
              </div>

              <div className="bg-muted rounded-xl p-8 mb-4">
                <div className="text-center text-muted-foreground">
                  <Grid3x3 className="h-16 w-16 mx-auto mb-4" />
                  <p className="font-medium">Grade do Caça-Palavras</p>
                  <p className="text-sm mt-2">Implementação completa em breve</p>
                  <p className="text-xs mt-4">Por enquanto, clique nas palavras acima para marcar como encontradas</p>
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  onClick={() => selectedPuzzle.palavras.forEach(p => handleWordFound(p))}
                  data-testid="wordsearch-find-all"
                  className="flex-1"
                  variant="outline"
                >
                  Simular Encontrar Todas
                </Button>
                <Button
                  onClick={finishGame}
                  data-testid="wordsearch-finish"
                  className="flex-1 bg-gradient-to-r from-primary to-secondary"
                >
                  Finalizar Jogo
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </Layout>
    );
  }

  const PuzzlesList = () => {
    const cards = [];
    for (let i = 0; i < puzzles.length; i++) {
      const puzzle = puzzles[i];
      cards.push(
        <motion.div
          key={puzzle.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.1 }}
          data-testid={`wordsearch-card-${i}`}
          className="p-6 rounded-2xl bg-white border border-border hover:shadow-lg transition-all"
        >
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4">
            <Grid3x3 className="h-6 w-6 text-white" />
          </div>
          <h3 className="text-xl font-bold mb-2">{puzzle.tema}</h3>
          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
            <span className="capitalize">{puzzle.dificuldade}</span>
            <span>{puzzle.palavras.length} palavras</span>
          </div>
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Até {puzzle.pontos_maximos} pontos</span>
          </div>
          <Button
            onClick={() => startGame(puzzle)}
            data-testid={`wordsearch-start-${i}`}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500"
          >
            <Play className="h-4 w-4 mr-2" />
            Jogar
          </Button>
        </motion.div>
      );
    }
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{cards}</div>;
  };

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="p-4 md:p-8 max-w-7xl mx-auto"
      >
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Caça-Palavras
          </h1>
          <p className="text-muted-foreground text-lg">Encontre as palavras escondidas</p>
        </div>

        <PuzzlesList />
      </motion.div>
    </Layout>
  );
}
